import React, { useEffect, useState } from 'react';
import { useParams } from 'react-router-dom';
import { MdAccountBalance, MdCheckCircle, MdError, MdLock } from 'react-icons/md';
import secureLocalStorage from 'react-secure-storage';
import { getInvitoPa, impostaPasswordInvito } from '../services/paApi';

function Card({ children }) {
  return (
    <div className="min-h-screen bg-gray-50 dark:bg-gray-900 flex items-center justify-center p-4">
      <div className="bg-white dark:bg-gray-800 rounded-2xl shadow-lg w-full max-w-md p-8">
        {children}
      </div>
    </div>
  );
}

const RUOLO_LABEL = {
  amministratore_ente: 'Amministratore Ente',
  dirigente:           'Dirigente',
  ufficio_tecnico:     'Ufficio Tecnico',
  ufficio_amministrativo: 'Ufficio Amministrativo',
};

export default function AccettaInvitoPaPage() {
  const { token } = useParams();

  const [invito,   setInvito]   = useState(null);
  const [stato,    setStato]    = useState('caricamento'); // caricamento | pronto | salvando | completato | errore | scaduto
  const [errore,   setErrore]   = useState('');
  const [form,     setForm]     = useState({ password: '', password_confirmation: '' });

  useEffect(() => {
    getInvitoPa(token)
      .then((data) => {
        if (!data.valido) { setStato('scaduto'); return; }
        setInvito(data);
        setStato('pronto');
      })
      .catch(() => setStato('errore'));
  }, [token]);

  const aggiorna = (e) => setForm((p) => ({ ...p, [e.target.name]: e.target.value }));

  const handleSubmit = async (e) => {
    e.preventDefault();
    if (form.password !== form.password_confirmation) {
      setErrore('Le password non coincidono.');
      return;
    }
    setErrore('');
    setStato('salvando');
    try {
      await impostaPasswordInvito(token, form.password, form.password_confirmation);

      // Ora che la password è impostata, usa il login Veda standard (email+password → sessione cookie)
      const backend = window.VEDA_API_URL || '';
      await fetch(`${backend}/sanctum/csrf-cookie`, { credentials: 'include' });
      const csrf = decodeURIComponent(
        document.cookie.split('; ').find((c) => c.startsWith('XSRF-TOKEN='))?.split('=')[1] ?? ''
      );
      const loginRes = await fetch(`${backend}/api/dashboard/login`, {
        method: 'POST',
        credentials: 'include',
        headers: {
          Accept: 'application/json',
          'Content-Type': 'application/json',
          'X-XSRF-TOKEN': csrf,
        },
        body: JSON.stringify({ email: invito.email, password: form.password }),
      });

      if (!loginRes.ok) {
        throw new Error('Accesso configurato ma login automatico fallito. Vai su /login e accedi manualmente.');
      }

      const shape = await loginRes.json();
      secureLocalStorage.setItem('veda_user', JSON.stringify(shape));

      setStato('completato');
      setTimeout(() => {
        const role = shape?.role;
        if (role === 'amministratore_ente') window.location.replace('/pa/ente');
        else window.location.replace('/ppp');
      }, 2500);
    } catch (err) {
      setErrore(err.message ?? 'Errore durante il salvataggio. Riprova.');
      setStato('pronto');
    }
  };

  if (stato === 'caricamento') {
    return (
      <Card>
        <div className="flex flex-col items-center gap-4 py-8">
          <div className="animate-spin rounded-full h-10 w-10 border-b-2 border-blue-600" />
          <p className="text-gray-500 dark:text-gray-400 text-sm">Caricamento invito...</p>
        </div>
      </Card>
    );
  }

  if (stato === 'errore') {
    return (
      <Card>
        <div className="flex flex-col items-center gap-4 text-center">
          <MdError className="text-5xl text-red-400" />
          <h1 className="text-xl font-bold text-gray-800 dark:text-white">Invito non valido</h1>
          <p className="text-gray-500 dark:text-gray-400 text-sm">
            {errore || 'Questo link non è valido o è scaduto. Chiedi all\'amministratore di inviarne uno nuovo.'}
          </p>
        </div>
      </Card>
    );
  }

  if (stato === 'scaduto') {
    return (
      <Card>
        <div className="flex flex-col items-center gap-4 text-center">
          <MdCheckCircle className="text-5xl text-gray-400" />
          <h1 className="text-xl font-bold text-gray-800 dark:text-white">Invito scaduto o già utilizzato</h1>
          <p className="text-gray-500 dark:text-gray-400 text-sm">
            Contatta l'amministratore della piattaforma per ricevere un nuovo invito.
          </p>
        </div>
      </Card>
    );
  }

  if (stato === 'completato') {
    return (
      <Card>
        <div className="flex flex-col items-center gap-4 text-center">
          <MdCheckCircle className="text-5xl text-green-500" />
          <h1 className="text-xl font-bold text-gray-800 dark:text-white">Accesso configurato!</h1>
          <p className="text-gray-500 dark:text-gray-400 text-sm">
            Benvenuto in <strong>{invito?.enteDenominazione}</strong>. Reindirizzamento in corso...
          </p>
        </div>
      </Card>
    );
  }

  return (
    <Card>
      <div className="flex flex-col items-center mb-6">
        <div className="w-16 h-16 rounded-2xl bg-blue-100 dark:bg-blue-900 flex items-center justify-center text-blue-600 dark:text-blue-300 mb-4">
          <MdAccountBalance size={32} />
        </div>
        <h1 className="text-xl font-bold text-gray-800 dark:text-white text-center">Sei stato invitato su</h1>
        <p className="text-2xl font-bold text-blue-600 dark:text-blue-400 text-center mt-1">
          {invito?.enteDenominazione}
        </p>
      </div>

      <div className="bg-gray-50 dark:bg-gray-700/50 rounded-xl p-4 mb-6 space-y-2">
        <div className="flex justify-between text-sm">
          <span className="text-gray-500 dark:text-gray-400">Email</span>
          <span className="font-medium text-gray-800 dark:text-white">{invito?.email}</span>
        </div>
        <div className="flex justify-between text-sm">
          <span className="text-gray-500 dark:text-gray-400">Ruolo</span>
          <span className="font-medium text-gray-800 dark:text-white">
            {RUOLO_LABEL[invito?.ruolo] ?? invito?.ruolo}
          </span>
        </div>
        <div className="flex justify-between text-sm">
          <span className="text-gray-500 dark:text-gray-400">Scade il</span>
          <span className="font-medium text-gray-800 dark:text-white">
            {invito?.expiresAt
              ? new Date(invito.expiresAt).toLocaleDateString('it-IT', { day: '2-digit', month: 'long', year: 'numeric' })
              : '—'}
          </span>
        </div>
      </div>

      <form onSubmit={handleSubmit} className="flex flex-col gap-4">
        <div>
          <p className="text-sm font-medium text-gray-700 dark:text-gray-300 mb-3 flex items-center gap-2">
            <MdLock /> Scegli la tua password per accedere
          </p>
          <div className="flex flex-col gap-3">
            <input
              type="password"
              name="password"
              value={form.password}
              onChange={aggiorna}
              placeholder="Password (min. 8 caratteri)"
              required
              minLength={8}
              className="w-full px-4 py-2.5 border border-gray-300 dark:border-gray-600 rounded-lg focus:outline-none focus:ring-2 focus:ring-blue-500 dark:bg-gray-700 dark:text-white text-sm"
            />
            <input
              type="password"
              name="password_confirmation"
              value={form.password_confirmation}
              onChange={aggiorna}
              placeholder="Conferma password"
              required
              className="w-full px-4 py-2.5 border border-gray-300 dark:border-gray-600 rounded-lg focus:outline-none focus:ring-2 focus:ring-blue-500 dark:bg-gray-700 dark:text-white text-sm"
            />
          </div>
        </div>

        {errore && (
          <p className="text-sm text-red-500 text-center">{errore}</p>
        )}

        <button
          type="submit"
          disabled={stato === 'salvando'}
          className="w-full inline-flex items-center justify-center gap-2 px-5 py-3 rounded-xl bg-blue-600 hover:bg-blue-700 disabled:opacity-60 text-white font-semibold transition-colors"
        >
          {stato === 'salvando' ? (
            <>
              <div className="animate-spin rounded-full h-4 w-4 border-b-2 border-white" />
              Configurazione in corso...
            </>
          ) : (
            'Accedi alla piattaforma'
          )}
        </button>
      </form>
    </Card>
  );
}
